import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Pin } from 'lucide-react';
import toast from 'react-hot-toast';
import { getPinnedMessages, unpinMessage, type PinnedMessageItem } from '../../services/chatChannelService';
import Avatar from '../common/Avatar';
import { useProfileModal } from '../common/ProfileModalProvider';

function formatDate(iso: string) {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours()}:${d.getMinutes().toString().padStart(2, '0')}`;
}

interface Props {
  channelId: string;
  isOpen: boolean;
  onClose: () => void;
  onJumpToMessage: (messageId: string) => void;
  currentUserRole: string | null;
  currentUserId: string | undefined;
}

export default function PinnedMessageList({ channelId, isOpen, onClose, onJumpToMessage, currentUserRole, currentUserId }: Props) {
  const queryClient = useQueryClient();
  const { openProfileModal } = useProfileModal();

  const { data } = useQuery({
    queryKey: ['pinnedMessages', channelId, 'full'],
    queryFn: () => getPinnedMessages(channelId, 1, 50),
    enabled: isOpen,
    staleTime: 15_000,
  });

  const pins = data?.pins ?? [];
  const total = data?.pagination?.total ?? 0;

  const canUnpin = (pin: PinnedMessageItem) => {
    if (currentUserRole === 'OWNER') return true;
    if (currentUserRole === 'ADMIN' && pin.pinnedBy.userId === currentUserId) return true;
    return false;
  };

  const handleUnpin = async (messageId: string) => {
    try {
      await unpinMessage(channelId, messageId);
      toast.success('메시지 고정이 해제되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['pinnedMessages', channelId] });
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '고정 해제에 실패했습니다.';
      toast.error(msg);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="absolute z-20 top-full left-0 right-0 bg-white shadow-lg rounded-b-lg border max-h-80 overflow-y-auto">
      <div className="flex items-center justify-between px-4 py-3 border-b sticky top-0 bg-white z-10">
        <h4 className="font-semibold text-sm flex items-center gap-1.5">
          <Pin className="size-4 text-yellow-600" />
          고정된 메시지 ({total})
        </h4>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100">
          <X className="size-4" />
        </button>
      </div>

      {pins.length === 0 && (
        <div className="px-4 py-6 text-center text-sm text-gray-400">고정된 메시지가 없습니다.</div>
      )}

      {pins.map((pin) => (
        <div
          key={pin.id}
          className="px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer"
          onClick={() => { onJumpToMessage(pin.messageId); onClose(); }}
        >
          <div className="flex items-center gap-2 mb-1">
            {/* CHG-131 REQ-CP-026: 작성자 아바타·이름 클릭 → 프로필 모달 (jump 동작과 분리) */}
            <span onClick={(e) => { e.stopPropagation(); openProfileModal(pin.message.sender.userId); }} className="shrink-0">
              <Avatar
                userId={pin.message.sender.userId}
                name={pin.message.sender.name}
                avatarUrl={pin.message.sender.profileImage}
                size="xs"
              />
            </span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); openProfileModal(pin.message.sender.userId); }}
              className="font-medium text-sm text-gray-900 hover:underline"
            >
              {pin.message.sender.name}
            </button>
            <span className="text-sm text-gray-400">{formatDate(pin.message.createdAt)}</span>
          </div>
          <p className="text-sm text-gray-700 line-clamp-2">{pin.message.content}</p>
          <div className="flex items-center justify-between mt-1">
            <span className="text-sm text-gray-400">
              고정: {pin.pinnedBy.name} &middot; {formatDate(pin.pinnedAt)}
            </span>
            {canUnpin(pin) && (
              <button
                onClick={(e) => { e.stopPropagation(); handleUnpin(pin.messageId); }}
                className="text-sm text-red-500 hover:text-red-700 flex items-center gap-0.5"
              >
                <Pin className="size-3" /> 해제
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
